import {objectFromEntries, pick} from '@benchling/js-utils';
import {BaseUnitType, Unit as CoreUnit, Term, canConvert, getConversionFactor} from '@benchling/units';
import {Decimal} from 'decimal.js';
import gql from 'graphql-tag';
import isEqual from 'react-fast-compare';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {MeasurementUnit} from 'constants/__generated/MeasurementUnit';
import * as MeasurementConstants from 'constants/MeasurementConstants';
import {getApolloClient} from 'graphql-client/ApolloClient';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {MeasurementUnitBySymbol} from 'inventory/__generated/MeasurementUnitBySymbol';
import {
  MeasurementUnitsQuery,
  MeasurementUnitsQueryVariables,
} from 'inventory/__generated/MeasurementUnitsQuery';
import {UnitBySymbolQuery, UnitBySymbolQueryVariables} from 'inventory/__generated/UnitBySymbolQuery';

export interface MeasurementWithNewUnit {
  value: number | null;
  unit: MeasurementUnit | null;
}

export const VOLUME_TERM = {value: BaseUnitType.LENGTH, exponent: 3};
export const VOLUME_DENOM_TERM = {value: BaseUnitType.LENGTH, exponent: -3};

export const MASS_TERM = {value: BaseUnitType.MASS, exponent: 1};
export const MASS_DENOM_TERM = {value: BaseUnitType.MASS, exponent: -1};

export const MOLES_TERM = {value: BaseUnitType.AMOUNT, exponent: 1};

export const MOLARITY_TERMS = [MOLES_TERM, VOLUME_DENOM_TERM];
export const DENSITY_TERMS = [MASS_TERM, VOLUME_DENOM_TERM];

function normalizeTerms(terms: Array<Term<BaseUnitType>>): Array<Term<BaseUnitType>> {
  // If terms came from a GQL query we need to avoid __typename interfering w/ the equality check
  return terms.map((t) => pick(t, 'value', 'exponent'));
}

export function massUnitTypePredicate(unitType: CoreUnit['unitType']): boolean {
  return isEqual(normalizeTerms(unitType.terms), [MASS_TERM]);
}

export function isMassUnit(unit: MeasurementUnit): boolean {
  return massUnitTypePredicate(unit.unitType);
}

export function molUnitTypePredicate(unitType: CoreUnit['unitType']): boolean {
  return isEqual(normalizeTerms(unitType.terms), [MOLES_TERM]);
}

export function volumeUnitTypePredicate(unitType: CoreUnit['unitType']): boolean {
  return isEqual(normalizeTerms(unitType.terms), [VOLUME_TERM]);
}

export function isVolumeUnit(unit: MeasurementUnit): boolean {
  return volumeUnitTypePredicate(unit.unitType);
}

export function isVolumeOrNullUnit(unit: MeasurementUnit | null): boolean {
  return unit == null || isVolumeUnit(unit);
}

export function molarityUnitTypePredicate(unitType: CoreUnit['unitType']): boolean {
  return isEqual(normalizeTerms(unitType.terms), MOLARITY_TERMS);
}

export function isMolarityUnit(unit: MeasurementUnit): boolean {
  return molarityUnitTypePredicate(unit.unitType);
}

export function densityUnitTypePredicate(unitType: CoreUnit['unitType']): boolean {
  return isEqual(normalizeTerms(unitType.terms), DENSITY_TERMS);
}

export function isDensityUnit(unit: MeasurementUnit): boolean {
  return densityUnitTypePredicate(unit.unitType);
}

function validContainerQuantityTerms(): Array<Array<Term<BaseUnitType>>> {
  return BenchlingConfig.INVENTORY_ENABLE_SOLID_UNITS_FOR_CONTAINER
    ? [[VOLUME_TERM], [MASS_TERM], [MOLES_TERM], []]
    : [[VOLUME_TERM]];
}

export function unitTypeFormulaMatchesContainerQuantity(unitType: CoreUnit['unitType']): boolean {
  return validContainerQuantityTerms().some((validTerms) =>
    isEqual(normalizeTerms(unitType.terms), validTerms)
  );
}

export function containerQuantityUnitTypePredicate(
  unitType: CoreUnit['unitType'] & {
    overrideIsConcentration?: boolean | null;
    overrideIsContainerQuantity?: boolean | null;
  }
): boolean {
  return (
    !unitType.overrideIsConcentration &&
    (unitTypeFormulaMatchesContainerQuantity(unitType) || Boolean(unitType.overrideIsContainerQuantity))
  );
}

/**
 * Given an array of Unit objects, returns those whose UnitType is valid for container quantities.
 */
export function filterToValidContainerQuantityUnits(units: Array<MeasurementUnit>): Array<MeasurementUnit> {
  return units.filter((u) => containerQuantityUnitTypePredicate(u.unitType));
}

export function unitTypeFormulaMatchesConcentration(unitType: CoreUnit['unitType']): boolean {
  return normalizeTerms(unitType.terms).some(
    (term) => isEqual(term, VOLUME_DENOM_TERM) || isEqual(term, MASS_DENOM_TERM)
  );
}

export function concentrationUnitTypePredicate(
  unitType: CoreUnit['unitType'] & {
    overrideIsConcentration?: boolean | null;
    overrideIsContainerQuantity?: boolean | null;
  }
): boolean {
  return (
    !unitType.overrideIsContainerQuantity &&
    (unitTypeFormulaMatchesConcentration(unitType) || Boolean(unitType.overrideIsConcentration))
  );
}

/**
 * Given an array of Unit objects, returns those whose UnitType is valid for concentration values.
 */
export function filterToValidConcentrationUnits(units: Array<MeasurementUnit>): Array<MeasurementUnit> {
  return units.filter((u) => concentrationUnitTypePredicate(u.unitType));
}

export function isConcentrationUnit(unit: MeasurementUnit): boolean {
  return concentrationUnitTypePredicate(unit.unitType);
}

function testCombinations<T>(items: Array<T>, condition: (a: T, b: T) => boolean): boolean {
  for (let i = 0; i < items.length; i++) {
    for (let j = 0; j < items.length; j++) {
      if (i !== j && !condition(items[i], items[j])) {
        return false;
      }
    }
  }
  return true;
}

export function areUnitsCompatible(units: Array<MeasurementUnit>): boolean {
  return testCombinations(
    units.map((u) => withDecimalFactor(u)),
    canConvert
  );
}

export function areUnitsCompatibleFromSymbols(symbols: Array<string | null>): boolean {
  const units = symbols
    .filter((symbol) => symbol != null)
    .map((symbol: string) => getUnitFromCacheBySymbol(symbol));
  return areUnitsCompatible(units);
}

export function getCompatibilityFilterFromSymbols(
  symbols: Array<string>
): ((unitType: CoreUnit['unitType'] & {id: string}) => boolean) | null {
  const units = symbols.map((symbol: string) => getUnitFromCacheBySymbol(symbol));
  const uniqTypeIds = new Set(units.map((unit) => unit.unitType.id));
  if (uniqTypeIds.size !== 1) {
    return null;
  }

  return (ut) => ut.id === Array.from(uniqTypeIds)[0];
}

type MeasurementUnitWithDecimalFactor = {
  [K in keyof MeasurementUnit]: K extends 'conversionFactor' ? Decimal : MeasurementUnit[K];
};

const withDecimalFactor = (unit: MeasurementUnit): MeasurementUnitWithDecimalFactor => ({
  ...unit,
  conversionFactor: new Decimal(unit.conversionFactor),
});

export function convertToStandard(measurement: MeasurementWithNewUnit): Decimal | null {
  if (measurement.value == null || measurement.unit == null) {
    return null;
  }

  const baseUnit = {
    name: '',
    symbol: '',
    conversionFactor: new Decimal('1'),
    unitType: measurement.unit.unitType,
  };

  const factor = getConversionFactor(withDecimalFactor(measurement.unit), baseUnit);
  return new Decimal(measurement.value).times(factor);
}

export function convertFromStandard(
  value: Decimal | null,
  unit: MeasurementUnit | null
): MeasurementWithNewUnit {
  if (value == null || unit == null) {
    return {value: null, unit};
  }

  const baseUnit = {
    name: '',
    symbol: '',
    conversionFactor: new Decimal('1'),
    unitType: unit.unitType,
  };

  const factor = getConversionFactor(baseUnit, withDecimalFactor(unit));
  return {
    value: value.times(factor).toNumber(),
    unit,
  };
}

export function convertToUnit(
  measurement: MeasurementWithNewUnit,
  newUnit: MeasurementUnit | null
): MeasurementWithNewUnit {
  if (measurement.unit == null || measurement.value == null || newUnit == null) {
    return {value: null, unit: null};
  }

  const factor = getConversionFactor(withDecimalFactor(measurement.unit), withDecimalFactor(newUnit));
  return {
    value: new Decimal(measurement.value).times(factor).toNumber(),
    unit: newUnit,
  };
}

export const MEASUREMENT_UNIT_BY_SYMBOL_FRAGMENT = gql`
  fragment MeasurementUnitBySymbol on UnitBySymbolNode {
    symbol
    unit {
      ...MeasurementUnit
    }
  }
  ${MeasurementConstants.MeasurementUnitFragment.fragment}
`;

/**
 * Loads a Unit object from the Apollo cache by its symbol.
 */
export function maybeGetUnitFromCacheBySymbol(symbol: string): MeasurementUnit | null {
  const client = getApolloClient();
  const data = client.readFragment<MeasurementUnitBySymbol>({
    fragment: MEASUREMENT_UNIT_BY_SYMBOL_FRAGMENT,
    fragmentName: 'MeasurementUnitBySymbol',
    id: client.cache.identify({__typename: 'UnitBySymbolNode', symbol}),
  });
  return data?.unit ?? null;
}

/**
 * Loads a Unit object from the Apollo cache by its symbol.
 *
 * IMPORTANT: Will error if the matching unit is not already present in the cache.
 */
export function getUnitFromCacheBySymbol(symbol: string): MeasurementUnit {
  const unit = maybeGetUnitFromCacheBySymbol(symbol);
  assert(unit != null, `Unit with symbol ${symbol} not found in cache`);
  return unit;
}

const UNIT_BY_SYMBOL_QUERY = gql`
  query UnitBySymbolQuery($symbol: StrictString!) @engComponent(name: UNITS) @skipAudit {
    unitBySymbol(symbol: $symbol) {
      symbol
      unit {
        ...MeasurementUnit
      }
    }
  }
  ${MeasurementConstants.MeasurementUnitFragment.fragment}
`;

/** Returns null for a symbol the backend does not recognize. */
export async function fetchUnitBySymbol(symbol: string): Promise<MeasurementUnit | null> {
  const data = await doQuerySafe<UnitBySymbolQuery, UnitBySymbolQueryVariables>(
    {query: UNIT_BY_SYMBOL_QUERY},
    {variables: {symbol}}
  );
  return data.unitBySymbol.unit;
}

/**
 * Loads the units for the given symbols, keyed by symbol. Symbols the backend does not recognize are
 * absent from the result, so callers must handle a missing symbol.
 *
 * There is no plural `unitBySymbol` field, so this issues one operation per distinct symbol. The
 * operations are coalesced into a single HTTP request by the Apollo batch link.
 */
export async function bulkFetchUnitsBySymbol(
  symbols: Array<string>
): Promise<{[symbol: string]: MeasurementUnit}> {
  const distinctSymbols = Array.from(new Set(symbols));
  const units = await Promise.all(distinctSymbols.map((symbol) => fetchUnitBySymbol(symbol)));
  return objectFromEntries(
    distinctSymbols.flatMap((symbol, index) => {
      const unit = units[index];
      return unit != null ? [[symbol, unit] as [string, MeasurementUnit]] : [];
    })
  );
}

const MEASUREMENT_UNITS_QUERY = gql`
  query MeasurementUnitsQuery($ids: [StrictString!]!) @engComponent(name: UNITS) @skipAudit {
    units(ids: $ids) {
      ...MeasurementUnit
    }
  }
  ${MeasurementConstants.MeasurementUnitFragment.fragment}
`;

export async function bulkFetchMeasurementUnits(ids: Array<string>): Promise<Array<MeasurementUnit>> {
  const data = await doQuerySafe<MeasurementUnitsQuery, MeasurementUnitsQueryVariables>(
    {query: MEASUREMENT_UNITS_QUERY},
    {variables: {ids}}
  );
  return data.units;
}
